/**
 * Bondruck und Kassenlade über die Hardware-Bridge, aus Sicht der Kasse:
 *
 * - **Automatischer Bondruck** nach dem Verkauf ist eine Einstellung je Kasse (auf diesem
 *   Gerät gespeichert). Jeder Beleg wird höchstens einmal automatisch gedruckt – auch nach
 *   Neuladen oder erneutem Öffnen der Belegansicht nicht noch einmal.
 * - **Nachdruck** nutzt immer den vorhandenen Beleg (`POST …/belege/:nr/druckauftrag`); es
 *   entsteht nie ein neuer RKSV-Beleg. Wurde der Beleg auf diesem Gerät schon erfolgreich
 *   gedruckt, trägt der Nachdruck „KOPIE“ im Druckbild (`?kopie=1`) – die Belegdaten bleiben
 *   unverändert. Schlug der erste Druck fehl, ist der Nachdruck das Original.
 * - Die Kasse wartet auf das Ergebnis der Bridge und zeigt es an (gedruckt, Druckerfehler,
 *   Bridge antwortet nicht) – kein Fehldruck bleibt unbemerkt.
 */
import type { Geraeteauftrag } from './api';
import type { Speicher } from './belegnummern';

/** Höchstzahl gemerkter Belegnummern je Liste (älteste fallen heraus). */
const MERKER_MAX = 500;

function schluessel(art: string, kassenId: string): string {
  return `bilanx.${art}.${kassenId}`;
}

function leseListe(speicher: Speicher, key: string): string[] {
  try {
    const roh = speicher.getItem(key);
    const daten: unknown = roh === null ? [] : JSON.parse(roh);
    return Array.isArray(daten) ? daten.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function fuegeHinzu(speicher: Speicher, key: string, wert: string): void {
  const liste = leseListe(speicher, key).filter((x) => x !== wert);
  liste.push(wert);
  try {
    speicher.setItem(key, JSON.stringify(liste.slice(-MERKER_MAX)));
  } catch {
    // Speicher voll oder gesperrt: dann eben ohne Merker (Nachdruck ohne „KOPIE“).
  }
}

/** Automatischer Bondruck nach dem Verkauf an dieser Kasse eingeschaltet? (Standard: aus) */
export function ladeAutoDruck(speicher: Speicher, kassenId: string): boolean {
  try {
    return speicher.getItem(schluessel('autodruck', kassenId)) === '1';
  } catch {
    return false;
  }
}

export function setzeAutoDruck(speicher: Speicher, kassenId: string, an: boolean): void {
  try {
    speicher.setItem(schluessel('autodruck', kassenId), an ? '1' : '0');
  } catch {
    // ohne Speicher bleibt die Einstellung aus
  }
}

/** Wurde der Beleg auf diesem Gerät schon erfolgreich gedruckt? (Dann ist ein Nachdruck eine Kopie.) */
export function istGedruckt(speicher: Speicher, kassenId: string, belegnummer: string): boolean {
  return leseListe(speicher, schluessel('gedruckt', kassenId)).includes(belegnummer);
}

export function merkeGedruckt(speicher: Speicher, kassenId: string, belegnummer: string): void {
  fuegeHinzu(speicher, schluessel('gedruckt', kassenId), belegnummer);
}

/**
 * Automatischen Druck für diesen Beleg beanspruchen: true genau beim ersten Aufruf je Beleg
 * (danach nie wieder – auch nicht nach Neuladen), damit kein Beleg doppelt gedruckt wird.
 */
export function beanspruecheAutoDruck(
  speicher: Speicher,
  kassenId: string,
  belegnummer: string,
): boolean {
  const key = schluessel('autodruck-versucht', kassenId);
  if (leseListe(speicher, key).includes(belegnummer)) {
    return false;
  }
  fuegeHinzu(speicher, key, belegnummer);
  return leseListe(speicher, key).includes(belegnummer);
}

/** Druckergebnis unbekannt (weder sicher gedruckt noch sicher fehlgeschlagen). */
export class DruckErgebnisUnklar extends Error {
  override readonly name = 'DruckErgebnisUnklar';
}

export interface GeraeteRueckmeldung {
  /** true = erledigt, false = fehlgeschlagen, null = Ergebnis (noch) unbekannt. */
  ok: boolean | null;
  text: string;
}

function ergebnisText(a: Geraeteauftrag): string | null {
  const e = a.details.ergebnis;
  if (typeof e === 'object' && e !== null && typeof (e as { text?: unknown }).text === 'string') {
    return (e as { text: string }).text;
  }
  return null;
}

/** Ergebniscode der Bridge: 0 = bestätigt, 1 = nur übergeben, 0xFE = unklar, 0xFF = nicht ausgeführt. */
function ergebnisCode(a: Geraeteauftrag): number | null {
  const e = a.details.ergebnis;
  const code =
    typeof e === 'object' && e !== null ? (e as { ergebnisCode?: unknown }).ergebnisCode : null;
  return typeof code === 'number' ? code : null;
}

/** Wartezeit der Kasse auf die Lade: länger als die Frist der Bridge (20 s), damit „verfällt“ stimmt. */
export const LADE_WARTE_MS = 40_000;

/** Verständlicher Text zum Stand eines Druck- oder Ladeauftrags. */
export function geraeteMeldung(
  a: Geraeteauftrag,
  was: 'bon' | 'lade' | 'test',
): GeraeteRueckmeldung {
  const text = ergebnisText(a);
  const name = was === 'lade' ? 'Kassenlade' : was === 'test' ? 'Testbon' : 'Bon';
  // Hinweis der Bridge nach „ – “ (z. B. „Papier fast leer“) in Klammern anhängen.
  const trenner = text?.indexOf(' – ') ?? -1;
  const zusatz = text !== null && trenner >= 0 ? ` (${text.slice(trenner + 3)})` : '';
  switch (a.status) {
    case 'erledigt': {
      // Code 1: an Drucker bzw. Windows-Warteschlange übergeben, Ausdruck nicht bestätigt.
      const bestaetigt = ergebnisCode(a) !== 1;
      return {
        ok: true,
        text:
          was === 'lade'
            ? `Kassenlade ${bestaetigt ? 'geöffnet' : 'angesteuert'}${zusatz}`
            : bestaetigt
              ? `${name} gedruckt${zusatz}`
              : `${name} an den Drucker übergeben – Ausdruck prüfen`,
      };
    }
    case 'fehlgeschlagen':
      if (ergebnisCode(a) === 0xfe) {
        // Ausgang unbekannt (Bridge-Neustart, Papierende im Druck, hängende Warteschlange)
        return {
          ok: null,
          text:
            was === 'lade'
              ? `Kassenlade: Ergebnis unklar – ${text ?? ''}`
              : `${name}: Ergebnis unklar – ${text ?? ''}. Nur nachdrucken, wenn nichts herauskam (Nachdruck trägt „KOPIE“).`,
        };
      }
      return {
        ok: false,
        text: `${was === 'lade' ? 'Kassenlade nicht geöffnet' : `${name} nicht gedruckt`}: ${text ?? 'unbekannter Fehler'}${was === 'bon' ? ' – Nachdruck möglich' : ''}`,
      };
    case 'uebernommen':
      return {
        ok: null,
        text: `${name}: Bridge arbeitet noch – Ergebnis am Drucker prüfen`,
      };
    case 'offen':
      return {
        ok: null,
        text:
          was === 'lade'
            ? 'Kassenlade: Bridge antwortet nicht – Lade mit dem Schlüssel öffnen; der Auftrag ist verfallen und öffnet sie nicht verspätet'
            : `${name}: Bridge antwortet nicht – Bridge-Dienst und Netzwerk prüfen (Auftrag verfällt nach 15 Minuten, dann Nachdruck)`,
      };
  }
}

export interface GeraeteApi {
  terminalAuftrag(kassenId: string, auftragId: string): Promise<Geraeteauftrag>;
}

/**
 * Auf das Ergebnis eines Geräteauftrags warten (Abfrage alle `intervallMs`, höchstens
 * `timeoutMs`). Liefert den letzten bekannten Stand – auch wenn er noch offen ist.
 */
export async function warteAufGeraeteauftrag(
  api: GeraeteApi,
  kassenId: string,
  auftrag: Geraeteauftrag,
  opt: { intervallMs?: number; timeoutMs?: number; signal?: AbortSignal } = {},
): Promise<Geraeteauftrag> {
  const frist = Date.now() + (opt.timeoutMs ?? 20_000);
  let stand = auftrag;
  while (stand.status === 'offen' || stand.status === 'uebernommen') {
    if (Date.now() >= frist || opt.signal?.aborted === true) {
      return stand;
    }
    await new Promise((r) => setTimeout(r, opt.intervallMs ?? 700));
    try {
      stand = await api.terminalAuftrag(kassenId, auftrag.auftragId);
    } catch {
      // Netzfehler: weiter versuchen bis zur Frist
    }
  }
  return stand;
}

export interface DruckApi extends GeraeteApi {
  druckauftrag(
    kassenId: string,
    belegnummer: string,
    opt?: { kopie?: boolean },
  ): Promise<Geraeteauftrag>;
  schubladeOeffnen(kassenId: string): Promise<Geraeteauftrag>;
}

/**
 * Beleg über die Bridge drucken und das Ergebnis abwarten. Kopie, wenn der Beleg auf diesem
 * Gerät schon erfolgreich gedruckt wurde.
 */
export async function druckeBeleg(
  api: DruckApi,
  speicher: Speicher,
  kassenId: string,
  belegnummer: string,
  opt: { timeoutMs?: number; intervallMs?: number } = {},
): Promise<GeraeteRueckmeldung> {
  const kopie = istGedruckt(speicher, kassenId, belegnummer);
  const auftrag = await api.druckauftrag(kassenId, belegnummer, kopie ? { kopie: true } : {});
  const ende = await warteAufGeraeteauftrag(api, kassenId, auftrag, opt);
  if (ende.status === 'erledigt') {
    merkeGedruckt(speicher, kassenId, belegnummer);
  }
  const r = geraeteMeldung(ende, 'bon');
  return kopie && r.ok === true ? { ...r, text: r.text.replace(/^Bon /, 'Kopie ') } : r;
}

/** Kassenlade öffnen und das Ergebnis abwarten (kurz: die Lade soll sofort aufgehen). */
export async function oeffneLade(
  api: DruckApi,
  kassenId: string,
  opt: { timeoutMs?: number; intervallMs?: number } = {},
): Promise<GeraeteRueckmeldung> {
  const auftrag = await api.schubladeOeffnen(kassenId);
  const ende = await warteAufGeraeteauftrag(api, kassenId, auftrag, {
    timeoutMs: LADE_WARTE_MS,
    intervallMs: 500,
    ...opt,
  });
  return geraeteMeldung(ende, 'lade');
}
